import styles from "./notifications.module.scss";
import {
  useAcceptInvitationMutation,
  useLazyGetPendingInvitationsQuery,
  useRejectInvitationMutation,
} from "@/redux/api/accountApi.ts";
import InvitationNotification from "./InvitationNotification.tsx";
import type { AccountInvite } from "@/redux/types.ts";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowClockwise";
import { BellIcon } from "@phosphor-icons/react/dist/csr/Bell";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Loader from "../../../views/common/loader/Loader.tsx";
import cn from "classnames";

const POLLING_INTERVAL = 15 * 60000; // 15 minutes

const {
  notificationDropdownContainer,
  notificationDropdownContainerActive,
  notificationBell,
  notificationBadge,
  notificationsContainer,
  notificationsHeader,
  noNotifications,
} = styles;

type NotificationsProps = {
  onOpen?: () => void;
};

const Notifications: FC<NotificationsProps> = ({ onOpen }) => {
  const { t } = useTranslation("translation", { keyPrefix: "notifications" });
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [acceptInvite] = useAcceptInvitationMutation();
  const [rejectInvite] = useRejectInvitationMutation();
  const [getInvites, { data, isFetching }] = useLazyGetPendingInvitationsQuery({
    pollingInterval: POLLING_INTERVAL,
  });
  const [open, setOpen] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);

  const closeDropdown = (e: MouseEvent) => {
    const path = e.composedPath();

    if (!dropdownRef.current || !anchorRef.current) {
      return;
    }

    if (path.includes(dropdownRef.current) || path.includes(anchorRef.current)) {
      return;
    }

    setOpen(false);
    setAnchor(null);
  };

  useEffect(() => {
    getInvites();
    document.addEventListener("click", closeDropdown);

    return () => {
      document.removeEventListener("click", closeDropdown);
    };
  }, []);

  const onRefresh = () => {
    getInvites();
  };

  const openDropdown = () => {
    setOpen(!open);
    setAnchor(anchorRef.current);
    onOpen?.();
  };

  const onAccept = (invite: AccountInvite) => {
    acceptInvite({ inviteId: invite.inviteId });
  };

  const onReject = (invite: AccountInvite) => {
    rejectInvite({ inviteId: invite.inviteId });
  };

  return (
    <div
      className={cn(notificationDropdownContainer, { [notificationDropdownContainerActive]: open })}
      data-testid="notification-dropdown"
    >
      <div data-testid="notification-button" className={notificationBell} onClick={openDropdown} ref={anchorRef}>
        <BellIcon size={24} className={notificationBell} />
        {data?.length ? <span className={notificationBadge}>{data.length}</span> : null}
      </div>
      <Popper placement="bottom" open={open} anchorEl={anchor} ref={dropdownRef} disablePortal>
        <div className={notificationsContainer}>
          <div className={notificationsHeader}>
            <span>{t("notifications")}</span>
            <Tooltip classes={{ popper: styles.tooltip }} title={t("refresh")} placement="bottom-end">
              <div className={styles.refreshButton} onClick={onRefresh}>
                <ArrowClockwiseIcon size={16} />
              </div>
            </Tooltip>
          </div>
          <div className={styles.notificationsList}>
            {isFetching ? (
              <Loader />
            ) : data?.length ? (
              data.map((invite: AccountInvite) => (
                <InvitationNotification key={invite.inviteId} invite={invite} onAccept={onAccept} onReject={onReject} />
              ))
            ) : (
              <div className={noNotifications}>{t("noNotifications")}</div>
            )}
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default Notifications;
